import type {
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { TAuthorData } from "./types";

import { useFiltersStorage } from "storage/filters";

const useGroupAuthorsFilters = (
  dataset: TAuthorData[],
  setFilteredDataset: Dispatch<SetStateAction<TAuthorData[]>>,
): IUseFilterProps<TAuthorData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const filterOptions: IOptionsProps<TAuthorData>[] = useMemo(
    (): IOptionsProps<TAuthorData>[] => [
      {
        filterOptions: [
          {
            key: "actor",
            label: t("group.authors.actor"),
            options: dataset.map(
              (value): IOptions => ({ label: value.actor, value: value.actor }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("group.authors.actor"),
      },
      {
        filterOptions: [
          {
            filterFn: "includesInsensitive",
            key: "groups",
            label: t("group.authors.groupsContributed"),
            type: "text",
          },
        ],
        label: t("group.authors.groupsContributed"),
      },
      {
        filterOptions: [
          {
            key: "repository",
            label: `${t("group.authors.repository")} is`,
            type: "text",
          },
          {
            filterFn: "includesInsensitive",
            key: "repository",
            label: `or ${t("group.authors.repository")} contains`,
            type: "text",
          },
        ],
        label: t("group.authors.repository"),
      },
    ],
    [dataset, t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("authors") as string | null;
  }, [getValues]);

  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("authors", value);
    },
    [setValues],
  );

  return useFilters({
    dataset,
    getValues: handleGetValues,
    options: filterOptions,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useGroupAuthorsFilters };
